import { useState } from "react";
import { useMutation } from "@apollo/client";
import { HANDLE_SYSTEM_MESSAGE_ACTION_MUTATION } from "../graphql/mutations";
import type {
  HandleSystemMessageActionMutation,
  SystemMessageActionType,
} from "../generated/graphql";

export interface UseSystemMessageActionOptions {
  actionData?: Record<string, unknown>;
  resourceId?: string;
  resourceType?: string;
}

export interface UseSystemMessageActionReturn {
  // Actions
  confirmLearning: (messageId: string, options?: UseSystemMessageActionOptions) => Promise<HandleSystemMessageActionMutation['handleSystemMessageAction']>;
  dismissLearning: (messageId: string, options?: UseSystemMessageActionOptions) => Promise<HandleSystemMessageActionMutation['handleSystemMessageAction']>;
  editLearning: (messageId: string, options?: UseSystemMessageActionOptions) => Promise<HandleSystemMessageActionMutation['handleSystemMessageAction']>;
  viewAction: (messageId: string, options?: UseSystemMessageActionOptions) => Promise<HandleSystemMessageActionMutation['handleSystemMessageAction']>;
  handleAction: (
    messageId: string,
    action: SystemMessageActionType,
    options?: UseSystemMessageActionOptions
  ) => Promise<HandleSystemMessageActionMutation['handleSystemMessageAction']>;

  // State
  loading: boolean;
  error: string | null;
}

export const useSystemMessageAction = (): UseSystemMessageActionReturn => {
  const [error, setError] = useState<string | null>(null);

  const [executeAction, { loading }] = useMutation(
    HANDLE_SYSTEM_MESSAGE_ACTION_MUTATION,
    {
      onCompleted: () => {
        setError(null);
      },
      onError: (error) => {
        setError(error.message);
      },
    }
  );

  const handleAction = async (
    messageId: string,
    action: SystemMessageActionType,
    options: UseSystemMessageActionOptions = {}
  ) => {
    try {
      setError(null);

      const result = await executeAction({
        variables: {
          input: {
            messageId,
            action,
            actionData: options.actionData || null,
            resourceId: options.resourceId || null,
            resourceType: options.resourceType || null,
          },
        },
      });

      if (result.data?.handleSystemMessageAction) {
        return result.data.handleSystemMessageAction;
      }

      throw new Error("No data returned from mutation");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(errorMessage);
      throw err;
    }
  };

  // Convenience methods for specific actions
  const confirmLearning = (messageId: string, options?: UseSystemMessageActionOptions) =>
    handleAction(messageId, "CONFIRM", options);

  const dismissLearning = (messageId: string, options?: UseSystemMessageActionOptions) =>
    handleAction(messageId, "DISMISS", options);

  const editLearning = (messageId: string, options?: UseSystemMessageActionOptions) =>
    handleAction(messageId, "EDIT", options);

  const viewAction = (messageId: string, options?: UseSystemMessageActionOptions) =>
    handleAction(messageId, "VIEW", options);

  return {
    // Actions
    confirmLearning,
    dismissLearning,
    editLearning,
    viewAction,
    handleAction,

    // State
    loading,
    error,
  };
};